'use client';

import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { resolveDesignOverride, type DesignOverride } from '@/lib/product-design';

/**
 * A product page can override the viewer's own starfield / accent-background
 * theme preferences, but ONLY while that page is open. The two background
 * effects are rendered once at the app shell (client-layout), so rather than
 * mounting a second copy on the product page, the product page publishes its
 * override here and the layout reads it. The ACCENT COLOUR, and the gradient's
 * colour and direction, are read from the same override by ThemeProvider,
 * which is the single writer of the accent and gradient CSS variables. The
 * override is cleared on unmount, so navigating away restores the buyer's own
 * preferences with no leak.
 *
 * The shape and the resolver live in src/lib/product-design.ts because they are
 * pure and therefore testable; both are re-exported here so every existing
 * import site keeps working.
 */
export { resolveDesignOverride };
export type { DesignOverride };

interface ProductDesignContextValue {
  override: DesignOverride;
  setOverride: (next: DesignOverride) => void;
  /**
   * True while the open page is still loading the design it will publish (the
   * product document, the seller's brand, the storefront's user document).
   * Until it lands the shell cannot tell "no override" from "not yet known",
   * and the viewer's effects may be on, so reading `override` alone paints
   * exactly what a seller switched off and then tears it down. The cosmic gate
   * (src/lib/cosmic-effects.ts) holds the effects while this is set. Only the
   * public pages set it; an editor preview publishes synchronously.
   */
  overridePending: boolean;
  setOverridePending: (pending: boolean) => void;
}

const ProductDesignContext = createContext<ProductDesignContextValue | undefined>(undefined);

export function ProductDesignProvider({ children }: { children: ReactNode }) {
  const [override, setOverrideState] = useState<DesignOverride>(null);
  // A raw state setter is already stable and already skips a same-value update,
  // which is all the page effects that call it on mount and unmount need.
  const [overridePending, setOverridePending] = useState(false);

  // Stable setter so the page-side effects that call it on mount/unmount don't
  // re-fire on every layout re-render.
  const setOverride = useCallback((next: DesignOverride) => {
    setOverrideState(prev => {
      // Avoid a state churn when the resolved override is unchanged.
      if (prev === next) return prev;
      // Every field the resolver produces has to be compared here. A field
      // missing from this list is one the page will never re-render for, and
      // the symptom is a setting that silently does nothing rather than an
      // error — see the matching dependency-array note in product-client.tsx.
      if (prev && next
          && prev.starfield === next.starfield
          && prev.accentBackground === next.accentBackground
          && prev.accentColor === next.accentColor
          && prev.theme === next.theme
          && prev.backgroundColor === next.backgroundColor
          && prev.gradientColor === next.gradientColor
          && prev.gradientDirection === next.gradientDirection) {
        return prev;
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ override, setOverride, overridePending, setOverridePending }),
    [override, setOverride, overridePending],
  );

  return (
    <ProductDesignContext.Provider value={value}>
      {children}
    </ProductDesignContext.Provider>
  );
}

export function useProductDesignOverride() {
  const ctx = useContext(ProductDesignContext);
  if (ctx === undefined) {
    throw new Error('useProductDesignOverride must be used within a ProductDesignProvider');
  }
  return ctx;
}
